import React, { useState } from "react";
import { useEscape } from "@/hooks/useEscape";
import { PenTool, Plus, Loader2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// The workspace's whiteboards. One name is all a whiteboard needs to exist.

export default function CanvasesView({ workspaceId, canvases, loading, users = [], onOpen, onCreated }) {
  const [creating, setCreating] = useState(false);
  useEscape(creating, () => setCreating(false));
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const nameOf = (id) => users.find((u) => u.id === id)?.name || "";

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/canvases", { workspace_id: workspaceId, name: name.trim() });
      setName(""); setCreating(false); onCreated(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Nu s-a putut crea"); }
    finally { setBusy(false); }
  };

  return (
    <div className="h-full flex flex-col min-h-0" data-testid="canvases-view">
      <div className="min-h-14 px-4 sm:px-6 py-2 flex items-center gap-3 border-b border-line/10 bg-bg">
        <PenTool className="w-4 h-4 text-brand" />
        <h2 className="font-bold tracking-tight">Whiteboard-uri</h2>
        <span className="text-fg/40 text-xs">{canvases.length}</span>
        <button type="button" data-testid="new-canvas" onClick={() => setCreating(true)} className="ml-auto btn-primary text-sm flex items-center gap-1.5 px-3 py-1.5">
          <Plus className="w-3.5 h-3.5" /> Whiteboard nou
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {loading && canvases.length === 0 ? (
          <p className="text-sm text-fg/40 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Se încarcă…</p>
        ) : canvases.length === 0 ? (
          <div className="text-center py-20 px-6 max-w-md mx-auto">
            <PenTool className="w-8 h-8 text-fg/20 mx-auto mb-3" />
            <p className="font-semibold">Niciun whiteboard încă</p>
            <p className="text-sm text-fg/50 mt-1 mb-4">O pânză infinită pentru schițe, diagrame și sticky notes, salvată pentru toată echipa.</p>
            <button type="button" onClick={() => setCreating(true)} className="btn-primary text-sm px-3 py-1.5">Creează primul whiteboard</button>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {canvases.map((c) => (
              <div key={c.id} className="border border-line/10 bg-surface hover:border-line/30 transition-colors group/cv relative">
                <button type="button" data-testid={`canvas-card-${c.id}`} onClick={() => onOpen(c.id)} className="w-full text-left p-4">
                  <p className="font-semibold truncate pr-6">{c.name}</p>
                  <p className="text-[11px] text-fg/50 mt-1">{c.element_count ?? 0} elemente · v{c.version ?? 0}{c.updated_by ? ` · ${nameOf(c.updated_by)}` : ""}</p>
                </button>
                <button type="button" onClick={() => window.open(`${window.location.origin}/?canvas=${c.id}&window=1`, "_blank", "noopener,width=1400,height=900")}
                  className="absolute top-3 right-3 p-1 text-fg/30 hover:text-brand opacity-0 group-hover/cv:opacity-100" title="Deschide în fereastră separată" aria-label="Deschide în fereastră separată">
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      {creating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onMouseDown={() => setCreating(false)} data-testid="new-canvas-dialog">
          <form onSubmit={submit} onMouseDown={(e) => e.stopPropagation()} className="w-full max-w-sm bg-surface border border-line/10 shadow-xl">
            <div className="px-4 py-3 border-b border-line/10 font-bold tracking-tight">Whiteboard nou</div>
            <div className="p-4 space-y-4">
              <input autoFocus data-testid="new-canvas-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="ex. Arhitectură Q4" className="input-flat text-sm py-1.5 w-full" />
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setCreating(false)} className="btn-ghost text-sm px-3 py-1.5">Anulează</button>
                <button type="submit" data-testid="new-canvas-submit" disabled={busy || !name.trim()} className="btn-primary text-sm px-3 py-1.5 disabled:opacity-50">Creează</button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
